// LazyRender.jsx
// A component that lazily renders a list of children based on their visibility in the viewport.

// Copyright HS Analysis GmbH, 2023
// Authors: Viktor Eberhardt, Valentin Haas

// Framework imports
import React from "react";
import { PropTypes } from "prop-types";

/**
 * Only load the visible children of a (long) list.
 * @param {Array} children Children to be rendered only whne in rendering area.
 * @param {number} maxContainerHeight The maximum allowed height of the container in pixels.
 * @param {number} elementHeight The height of a single element. all elements assumed to be the same height.
 * @param {number} offsetElements Optional. The number of elements to be preloaded outside of visible area. Defaults to 2.
 * @returns
 */
const LazyRender = React.forwardRef((props, ref) => {
  const {
    children,
    maxContainerHeight,
    elementHeight,
    offsetElements = 2,
  } = props;
  const containerRef = React.useRef();

  const [scrollPositionTop, setScrollPositionTop] = React.useState(0);
  const [containerHeight, setContainerHeight] = React.useState(100);

  React.useEffect(() => {
    setContainerHeight(containerRef.current.clientHeight);
    containerRef.current.addEventListener("scroll", (e) => handleScroll(e));
    return containerRef.current.removeEventListener("scroll", (e) =>
      handleScroll(e)
    );
  }, []);

  React.useEffect(() => {
    setContainerHeight(containerRef.current.clientHeight);
  }, [children.length]);

  /**
   * Update position and container properties on scroll.
   * @param {event} e Scrollevent.
   */
  const handleScroll = (e) => {
    setScrollPositionTop(e.target.scrollTop);
  };

  /**
   * Scrolls smoothly to the bottom of the list
   */
  const scrollBottom = () => {
    if (containerRef.current) {
      const { scrollHeight, clientHeight } = containerRef.current;
      containerRef.current.scrollTo({
        top: scrollHeight - clientHeight,
      });
    }
  };

  /**
   * The placeholder to put in place of a non-rendered element
   */
  const placeholder = (key) => (
    <li key={key} style={{ height: elementHeight }}></li>
  );

  /**
   * Determine if an element should be rendered depending on its position.
   * @param {int} idx The index of the element in an arrray.
   * @returns {Boolean} Whether an element sould be rendered.
   */
  function isInView(idx) {
    const pos = idx * elementHeight;

    const minRendered = scrollPositionTop - offsetElements * elementHeight;
    const maxRendered =
      scrollPositionTop + containerHeight + offsetElements * elementHeight;

    const shouldBeRendered = pos >= minRendered && pos <= maxRendered;
    return shouldBeRendered;
  }

  /**
   * Scrolls to a specific index in the list only if it's not already visible
   * @param {number} index The index to scroll to
   */
  const scrollToIndex = (index) => {
    if (containerRef.current) {
      const targetPosition = index * elementHeight;
      const currentScrollTop = containerRef.current.scrollTop;
      const containerHeight = containerRef.current.clientHeight;

      // Check if target is outside visible area
      const isBelowView = targetPosition > currentScrollTop + containerHeight;
      const isAboveView = targetPosition < currentScrollTop;

      if (isBelowView || isAboveView) {
        containerRef.current.scrollTo({
          top: targetPosition,
          behavior: "smooth",
        });
      }
    }
  };

  // make scrollBottom() callable from parent component
  React.useImperativeHandle(ref, () => ({
    scrollBottom,
    scrollToIndex,
  }));

  return (
    <div
      style={{
        overflow: "auto",
        maxHeight: maxContainerHeight,
      }}
      ref={containerRef}
    >
      {children.map((c, i) => (isInView(i) ? c : placeholder(i)))}
    </div>
  );
});

LazyRender.propTypes = {
  children: PropTypes.array.isRequired,
  maxContainerHeight: PropTypes.number.isRequired,
  elementHeight: PropTypes.number.isRequired,
  offsetElements: PropTypes.number,
};

LazyRender.displayName = "LazyRender";
export default LazyRender;
